import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN: string = (import.meta.env.MAPBOX_TOKEN as string) ?? '';
mapboxgl.accessToken = MAPBOX_TOKEN;

/**
 * Read-only static-feeling map for the listing detail page. Renders a
 * neighbourhood-radius circle around either the precise lat/lng (post-booking
 * for the renter, always for the owner/admin) or the privacy-fuzzed
 * coordinate (everyone else). No pin — just the circle — so we don't imply
 * the trailer lives at a specific dot.
 */
export function ListingLocationMap({
  lat,
  lng,
  precise,
}: {
  lat: number;
  lng: number;
  precise: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (!ref.current || !MAPBOX_TOKEN || mapRef.current) return;
    const map = new mapboxgl.Map({
      container: ref.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [lng, lat],
      zoom: precise ? 14 : 12,
      interactive: true,
      attributionControl: false,
    });
    mapRef.current = map;
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');

    map.on('load', () => {
      if (precise) {
        // Owner/post-booking view — a single pin on the exact spot.
        new mapboxgl.Marker({ color: '#ff751f' })
          .setLngLat([lng, lat])
          .addTo(map);
      } else {
        // Public view — a fuzz circle (~600 m radius) instead of a pin so we
        // don't imply this is THE driveway. The center is already a 200-600m
        // server-side offset; the circle communicates "somewhere in this area."
        map.addSource('pickup-area', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [lng, lat] },
            properties: {},
          },
        });
        map.addLayer({
          id: 'pickup-area-fill',
          type: 'circle',
          source: 'pickup-area',
          paint: {
            'circle-radius': {
              stops: [
                [10, 30],
                [14, 100],
                [16, 220],
              ],
            },
            'circle-color': '#ff751f',
            'circle-opacity': 0.18,
            'circle-stroke-color': '#ff751f',
            'circle-stroke-width': 2,
            'circle-stroke-opacity': 0.5,
          },
        });
      }
    });

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, [lat, lng, precise]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="w-full h-[260px] flex items-center justify-center bg-navy-50 text-navy-500 text-xs text-center p-4 rounded-lg">
        Map unavailable — <code className="mx-1">MAPBOX_TOKEN</code> not configured.
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className="w-full h-[260px] rounded-lg overflow-hidden border border-navy-100"
    />
  );
}
